// /admin/reference — справочники установки: категории материалов, единицы
// учёта, виды шаблонов печати.  Строки приходят из паков техпроцессов и
// правятся здесь.  Право: cycle.manage.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { referenceApi } from "@/lib/api";
import type { ReferenceList } from "@/lib/types";

const LISTS: ReferenceList[] = ["material_category", "unit", "print_kind"];
const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";

export default function ReferenceLists() {
  const { t } = useTranslation();
  const [list, setList] = useState<ReferenceList>("material_category");
  return (
    <>
      <PageHeader eyebrow="admin · reference" title={t("reference.title")} description={t("reference.description")} />
      <div className="flex flex-wrap gap-2 mb-4">
        {LISTS.map((l) => (
          <button
            key={l}
            className={`px-3 py-2 rounded-md border text-sm ${
              list === l ? "border-accent bg-accent/10 text-accent-strong" : "border-border bg-surface text-text-secondary"
            }`}
            onClick={() => setList(l)}
          >
            {t(`reference.lists.${l}`)}
          </button>
        ))}
      </div>
      <ListEditor key={list} list={list} />
    </>
  );
}

function ListEditor({ list }: { list: ReferenceList }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState({ code: "", ru: "", en: "" });
  const q = useQuery({ queryKey: ["reference", list], queryFn: () => referenceApi.list(list) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["reference", list] });
  const onError = (e: unknown) => setErr(e instanceof Error ? e.message : String(e));

  const save = useMutation({
    mutationFn: (v: { code: string; ru: string; en: string; sort?: number }) =>
      referenceApi.upsert(list, v.code, {
        label: Object.fromEntries(
          Object.entries({ ru: v.ru.trim(), en: v.en.trim() }).filter(([, x]) => x !== ""),
        ),
        ...(v.sort !== undefined ? { sort_order: v.sort } : {}),
      }),
    onSuccess: () => {
      setErr(null);
      setDraft({ code: "", ru: "", en: "" });
      invalidate();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (code: string) => referenceApi.remove(list, code),
    onSuccess: invalidate,
    onError,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const items = q.data!;

  return (
    <div className="space-y-4">
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt">
            <tr className="text-left text-text-muted font-mono text-xs uppercase">
              <th className="px-4 py-3">{t("reference.code")}</th>
              <th className="px-4 py-3">RU</th>
              <th className="px-4 py-3">EN</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <Row
                key={it.code}
                code={it.code}
                ru={it.label.ru ?? ""}
                en={it.label.en ?? ""}
                busy={save.isPending || remove.isPending}
                onSave={(ru, en) => save.mutate({ code: it.code, ru, en, sort: it.sort_order })}
                onDelete={() => {
                  if (window.confirm(t("reference.confirmDelete", { code: it.code }))) remove.mutate(it.code);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
      <form
        className="card p-4 grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr_auto] gap-3 items-end"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ ...draft, code: draft.code.trim(), sort: (items.at(-1)?.sort_order ?? 0) + 10 });
        }}
      >
        <input
          className={`${INPUT_CLS} font-mono`}
          placeholder={t("reference.code")}
          value={draft.code}
          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          required
        />
        <input
          className={INPUT_CLS}
          placeholder="RU"
          value={draft.ru}
          onChange={(e) => setDraft({ ...draft, ru: e.target.value })}
          required
        />
        <input className={INPUT_CLS} placeholder="EN" value={draft.en} onChange={(e) => setDraft({ ...draft, en: e.target.value })} />
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          + {t("common.add")}
        </button>
      </form>
    </div>
  );
}

function Row({
  code,
  ru,
  en,
  busy,
  onSave,
  onDelete,
}: {
  code: string;
  ru: string;
  en: string;
  busy: boolean;
  onSave: (ru: string, en: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState({ ru, en });
  const dirty = v.ru !== ru || v.en !== en;
  return (
    <tr className="border-t border-border">
      <td className="px-4 py-2 font-mono text-accent-strong">{code}</td>
      <td className="px-4 py-2">
        <input className={INPUT_CLS} value={v.ru} onChange={(e) => setV({ ...v, ru: e.target.value })} />
      </td>
      <td className="px-4 py-2">
        <input className={INPUT_CLS} value={v.en} onChange={(e) => setV({ ...v, en: e.target.value })} />
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {dirty && (
          <button className="btn text-xs mr-2" disabled={busy} onClick={() => onSave(v.ru, v.en)}>
            {t("common.save")}
          </button>
        )}
        <button className="btn btn-danger text-xs" disabled={busy} onClick={onDelete}>
          {t("common.delete")}
        </button>
      </td>
    </tr>
  );
}
